import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './ui/Button';
import { isChunkLoadError, reloadOnceForChunkError } from '../lib/chunkReload';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional reset callback (e.g. clear React Query caches before re-render). */
  onReset?: () => void;
  /** Telemetry hook — called with the captured error + React's componentStack
   *  so a future Sentry/Rollbar integration can drop in without touching
   *  every consumer. Returning a value is ignored. */
  onError?: (error: Error, info: ErrorInfo) => void;
  /** Where the "Go home" CTA navigates to. Defaults to /. Distinct
   *  defaults let the dashboard send users to /dashboard/ and the
   *  portal to /portal/. */
  homePath?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Generic React error boundary.  Catches render-time exceptions in any
 * descendant and shows a friendly fallback page that matches the brand,
 * instead of letting the React error overlay blank the whole SPA.
 *
 * Errors are logged to the console (Loki picks them up via promtail in
 * production) so the team has a forensic trail.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // A stale-bundle chunk failure (the dashboard was redeployed while this tab
    // was open) is transient — reload once to pull the fresh bundle instead of
    // stranding the candidate on the crash screen. Guarded against loops; if we
    // already reloaded recently we fall through and show the error screen.
    if (isChunkLoadError(error) && reloadOnceForChunkError()) return;
    // eslint-disable-next-line no-console
    console.error('Prism SPA crash:', error, info);
    this.props.onError?.(error, info);
  }

  handleReset = () => {
    this.props.onReset?.();
    this.setState({ error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex min-h-[60vh] items-center justify-center px-6 py-12">
        <div className="w-full max-w-[520px] text-center prism-enter">
          <div
            aria-hidden="true"
            className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-[6px] bg-[var(--color-danger-soft)] text-[var(--color-danger)]"
          >
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <circle cx="12" cy="16" r="0.6" fill="currentColor" />
            </svg>
          </div>
          <p className="eyebrow mb-3">Something went wrong</p>
          <h1 className="text-[26px] font-bold leading-tight tracking-[-0.02em] text-[var(--color-ink)]">
            This Page Crashed.
          </h1>
          <p className="mt-3 font-sans text-[14.5px] leading-relaxed text-[var(--color-ink-soft)]">
            We logged the error and the team has been notified. Try resetting
            this page; if it keeps happening, reload the app.
          </p>

          {/* Show the error message (not the stack — too noisy for users) */}
          <p className="mt-5 break-all rounded-[4px] border border-[var(--color-rule)] bg-[var(--color-surface-muted)] px-4 py-3 text-left font-mono text-[12px] text-[var(--color-ink-muted)]">
            {this.state.error.message || 'Unknown error'}
          </p>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button
              variant="ghost"
              size="md"
              onClick={() => {
                window.location.href = this.props.homePath ?? '/';
              }}
            >
              Go home
            </Button>
            <Button variant="secondary" size="md" onClick={this.handleReload}>
              Reload app
            </Button>
            <Button variant="primary" size="md" onClick={this.handleReset}>
              Reset this page
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
